import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import createForm from '@swf/components/Forms/Form';
import Input from '@swf/components/Inputs/Input';
import Button from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import type { Article } from '../../../../types';

import {
  TXTCLI_Comment,
  TXTCLI_No,
  TXTCLI_Submit,
  TXTCLI_Yes,
  TXTWCL_Did_you_find_this_article_usef_ELL,
  TXTWCL_Sorry_to_hear_thatX_please_can_ELL,
  TXTWCL_Tell_us_moreXX,
  TXTWCL_Thank_you_for_your_feedback_to_ELL,
  TXTWCL_We_did_goodX
} from './Feedback.messages.json';
import styles from './Feedback.scss';

interface Props {
  article: Article;
}

interface FormValues {
  comment: string;
  locale: string;
  articleRef: string;
  title: string;
  category: string | undefined;
  articleId: string;
}

const Form = createForm<FormValues>();

const Feedback: React.FC<Props> = ({ article = {} as Article }) => {
  const [submitted, setSubmitted] = useState(Boolean);
  const [selected, setSelected] = useState<'yes' | 'no' | ''>('');
  const [comment] = useState(String);

  const request = useRequest();
  const { formatMessage } = useIntl();

  const initialValues: FormValues = {
    comment: '',
    locale: useSelector(select.locale.get),
    articleRef: article.ref,
    title: article.title,
    category: article.category,
    articleId: article.id
  };

  useEffect(() => {
    setSelected('');
    setSubmitted(false);
  }, [article.ref]);

  const handleClick = async (showComments: boolean) => {
    if (showComments) {
      setSelected('no');
    } else {
      setSelected('yes');
    }

    request({
      service: 'help.feedbackManager.useful',
      body: { useful: !showComments }
    });
  };

  const handleSubmit = async (formValues: FormValues) => {
    setSubmitted(true);

    if (formValues.comment === '') {
      return;
    }

    request({
      service: 'help.feedbackManager.submit',
      body: { ...formValues, url: window.location.href }
    });
  };

  return submitted ? (
    <h2 className={styles.feedbackTitle}>
      😎
      <Translation {...TXTWCL_Thank_you_for_your_feedback_to_ELL} />
    </h2>
  ) : (
    <>
      <h2 className={styles.feedbackTitle}>
        <Translation {...TXTWCL_Did_you_find_this_article_usef_ELL} />
      </h2>
      <br />
      <Form className={styles.feedbackForm} onSubmit={handleSubmit} initialValues={initialValues}>
        <div>
          <ul className={styles.feedbackRadio}>
            <li>
              <input
                type="radio"
                id="useful_yes"
                name="useful_option"
                disabled={selected !== ''}
                checked={selected === 'yes'}
                onChange={() => handleClick(false)}
              />
              <label htmlFor="useful_yes">
                <Translation {...TXTCLI_Yes} />
              </label>
            </li>
            <li>
              <input
                type="radio"
                id="useful_no"
                name="useful_option"
                disabled={selected !== ''}
                checked={selected === 'no'}
                onChange={() => handleClick(true)}
              />
              <label htmlFor="useful_no">
                <Translation {...TXTCLI_No} />
              </label>
            </li>
          </ul>
        </div>
        {selected === 'yes' && (
          <p className={styles.furtherHelpText}>
            😊&nbsp;
            <Translation {...TXTWCL_We_did_goodX} />{' '}
            <Translation {...TXTWCL_Thank_you_for_your_feedback_to_ELL} />
          </p>
        )}
        {selected === 'no' && (
          <>
            <p className={styles.furtherHelpText}>
              😩&nbsp;
              <Translation {...TXTWCL_Sorry_to_hear_thatX_please_can_ELL} />
            </p>
            <Input
              className={styles.feedbackCommentBox}
              id="comment"
              type="textarea"
              label={TXTCLI_Comment}
              hideLabel
              value={comment}
              placeholder={formatMessage(TXTWCL_Tell_us_moreXX)}
            />
            <br />
            <Button
              type="submit"
              size="small"
              variant="secondary"
              translation={TXTCLI_Submit}
              className={styles.button}
            />
          </>
        )}
      </Form>
    </>
  );
};

export default Feedback;
